// Rules of the "Descargar stock de empleados" filters (broker and HR views).
//
// - Filters hidden: download everything.
// - Filters shown: start unchecked, with no date and no policies; download is
//   enabled once a policy type is checked, a date is chosen or a policy is
//   selected.
// - The date is never set automatically: checking a policy type filters by
//   policy only, so the counts match the "ESTADO POLIZA" column of the full
//   file.
// - Without selected policies, the policies of the checked types are sent;
//   with both types (or none) checked, no policy filter is sent.

export const POLICY_STATUS = Object.freeze({
  ACTIVE: 'Vigente',
  INACTIVE: 'No vigente'
});

export const DOWNLOAD_DISABLED_REASON =
  'Marca un tipo de póliza o elige una fecha';

export const NO_POLICIES_OF_TYPE_REASON =
  'No hay pólizas del tipo marcado para esta empresa';

const ALL_STATUSES = [POLICY_STATUS.ACTIVE, POLICY_STATUS.INACTIVE];

const isSingleStatus = (statuses = []) =>
  statuses.length === 1 && ALL_STATUSES.includes(statuses[0]);

export const policiesOfStatuses = (policies = [], statuses = []) =>
  policies.filter((policy) => statuses.includes(policy.status));

// Drops selected policies whose type is no longer checked.
export const pruneSelectedPolicies = (
  selectedPolicyIds = [],
  policies = [],
  statuses = []
) => {
  const allowed = new Set(
    policiesOfStatuses(policies, statuses).map((policy) => policy.id)
  );
  return selectedPolicyIds.filter((id) => allowed.has(id));
};

/**
 * Whether the download can run and, when it cannot, why.
 *
 * @returns {{ enabled: boolean, reason: string }}
 */
export const getDownloadState = ({
  filtersVisible,
  statuses = [],
  hasDate,
  selectedPolicyIds = [],
  policies = []
}) => {
  if (!filtersVisible) return { enabled: true, reason: '' };
  // Selected ids missing from the current list (e.g. left over from another
  // company) don't count.
  const validSelectedIds = pruneSelectedPolicies(
    selectedPolicyIds,
    policies,
    statuses
  );
  if (!statuses.length && !hasDate && !validSelectedIds.length) {
    return { enabled: false, reason: DOWNLOAD_DISABLED_REASON };
  }
  if (
    isSingleStatus(statuses) &&
    !validSelectedIds.length &&
    !policiesOfStatuses(policies, statuses).length
  ) {
    return { enabled: false, reason: NO_POLICIES_OF_TYPE_REASON };
  }
  return { enabled: true, reason: '' };
};

/**
 * Policy ids and date to send for the current filters.
 *
 * @param {Object} filters
 * @param {boolean} filters.filtersVisible
 * @param {string[]} filters.statuses - Checked policy types.
 * @param {number[]} filters.selectedPolicyIds
 * @param {string|null} filters.activeDate - Date as YYYY-MM-DD.
 * @param {Array<{ id: number, status: string }>} filters.policies
 * @returns {{ policyIds?: number[], activeDate?: string }}
 */
export const buildStockDownloadParams = ({
  filtersVisible,
  statuses = [],
  selectedPolicyIds = [],
  activeDate = null,
  policies = []
}) => {
  if (!filtersVisible) return {};

  const validSelectedIds = pruneSelectedPolicies(
    selectedPolicyIds,
    policies,
    statuses
  );
  let policyIds;
  if (validSelectedIds.length) {
    policyIds = validSelectedIds;
  } else if (isSingleStatus(statuses)) {
    policyIds = policiesOfStatuses(policies, statuses).map(
      (policy) => policy.id
    );
  }

  return {
    ...(policyIds ? { policyIds } : {}),
    ...(activeDate ? { activeDate } : {})
  };
};
